import {
  HollanderInterchangeEntry,
  PartsInquiryRequest,
  PartsInquiryFormat,
} from '../types/bom';

/**
 * Authentic Classic Car Hollander Interchange Database
 * Royal Hollander salvage yard interchange cross-reference covering classic American
 * muscle, pony cars, and light trucks (GM A/F/X bodies, Ford Mustang/Fairlane, Chrysler B/E bodies).
 */
export const HOLLANDER_INTERCHANGE_DB: HollanderInterchangeEntry[] = [
  // --- STEERING SYSTEMS ---
  {
    hollanderNumber: '560-00431',
    category: 'Steering Gear',
    description: 'Saginaw 800 Power Steering Gearbox - 12.7:1 Quick Ratio (3-Turn Lock-to-Lock)',
    yearSpan: '1968-1977',
    compatibleVehicles: [
      '1968-1972 Chevrolet Chevelle / El Camino / Monte Carlo',
      '1968-1972 Pontiac GTO / LeMans / Tempest',
      '1968-1972 Oldsmobile Cutlass / 442',
      '1968-1972 Buick Skylark / GS',
      '1969-1974 Chevrolet Nova / Chevy II',
      '1969-1974 Pontiac Ventura',
      '1970-1981 Chevrolet Camaro / Pontiac Firebird',
      '1982-1992 Chevrolet Camaro (Z28 / IROC-Z fast ratio retrofit)',
      '1993-1996 Chevrolet Caprice / Impala SS (9C1 Police pkg core donor)',
    ],
    interchangeNotes:
      'Direct bolt-in for 1964-1977 GM A/F/X-body frames using standard 3-bolt or 4-bolt Saginaw bosses. Metric O-ring to inverted flare adapters required when mating to pre-1980 high-pressure lines. 3/4-30 spline rag joint shaft required on early 13/16-36 columns.',
    crossReferenceOEMs: ['GM 7806396', 'GM 7812145', 'Cardone 27-6550'],
  },
  {
    hollanderNumber: '560-00128',
    category: 'Steering Gear',
    description: 'Saginaw Manual Steering Gearbox - 16:1 Quick Ratio (Cast Iron Case)',
    yearSpan: '1965-1974',
    compatibleVehicles: [
      '1967-1969 Chevrolet Camaro',
      '1968-1974 Chevrolet Nova',
      '1965-1972 Chevrolet Chevelle (Manual option)',
      '1967-1969 Pontiac Firebird',
    ],
    interchangeNotes:
      'Highly desirable lightweight drag and road-race manual conversion. 4-bolt frame mount. Input shaft is 3/4-36 spline. Pitman arm must match A-body vs F-body center link geometry.',
    crossReferenceOEMs: ['GM 5689014', 'Lares 10051'],
  },
  {
    hollanderNumber: '560-00512',
    category: 'Steering Gear',
    description: 'Chrysler B/E-Body Power Steering Gearbox - Firm Feel Stage 2/3 Valve',
    yearSpan: '1966-1974',
    compatibleVehicles: [
      '1966-1974 Dodge Charger / Coronet / Super Bee',
      '1970-1974 Dodge Challenger',
      '1966-1974 Plymouth Road Runner / GTX / Satellite',
      '1970-1974 Plymouth Barracuda / Cuda',
    ],
    interchangeNotes:
      'Standard small-sector (1-1/8") or large-sector (1-1/4" post-1972) output shaft. Cross-references between all B and E body platforms; requires matching pitman arm for K-frame steering geometry.',
    crossReferenceOEMs: ['Mopar 2823841', 'Mopar 3412500'],
  },

  // --- REAR AXLES & DIFFERENTIALS ---
  {
    hollanderNumber: '190-00812',
    category: 'Rear Axle & Differential',
    description: 'GM Corporate 8.5" 10-Bolt Posi-Traction Rear Axle Assembly (28-Spline)',
    yearSpan: '1971-1977',
    compatibleVehicles: [
      '1971-1972 Chevrolet Chevelle / Monte Carlo / El Camino',
      '1971-1972 Oldsmobile Cutlass (with corporate 10-bolt)',
      '1971-1972 Pontiac LeMans / GTO',
      '1972-1975 Chevrolet Nova (multi-leaf spring perches)',
      '1973-1977 GM A-Body Intermediate Platforms',
    ],
    interchangeNotes:
      'Bolt-in replacement for fragile GM 8.2" 10-bolt axles. Features 1.625" pinion stem and heavy-duty 8.5" ring gear comparable in strength to a 12-bolt. Verify spring perches: A-body utilizes 4-link upper/lower trailing arm mounts, while X-body uses leaf perches.',
    crossReferenceOEMs: ['GM 1234415', 'Eaton 19557-010', 'Moser 8.5-GM'],
  },
  {
    hollanderNumber: '190-00540',
    category: 'Rear Axle & Differential',
    description: 'GM 12-Bolt Passenger Car Differential Housing (8.875" Ring Gear, 30-Spline)',
    yearSpan: '1965-1972',
    compatibleVehicles: [
      '1965-1972 Chevrolet Chevelle SS / El Camino',
      '1970-1972 Chevrolet Monte Carlo SS 454',
      '1967-1969 Chevrolet Camaro Z28 / SS 396 (requires narrow 54.25" leaf perch width)',
      '1968-1972 Oldsmobile 442 (W-30 option)',
      '1970-1972 Pontiac GTO (455 HO option)',
    ],
    interchangeNotes:
      'Premier muscle car differential. Chevelle 1968-1972 housing measures 61.0" drum-to-drum width (1" wider than 1964-1967). Truck 12-bolt housings use smaller 1.438" pinion and 6-lug axles, and DO NOT interchange with passenger car housings.',
    crossReferenceOEMs: ['GM 3959038', 'GM 3894939', 'Strange PRG110'],
  },
  {
    hollanderNumber: '190-01120',
    category: 'Rear Axle & Differential',
    description: 'Ford 9-Inch Heavy-Duty Rear Axle Housing (31-Spline Large Bearing)',
    yearSpan: '1957-1986',
    compatibleVehicles: [
      '1967-1970 Ford Mustang (HiPo / Boss / Mach 1 / Cobra Jet)',
      '1967-1970 Mercury Cougar XR7',
      '1966-1971 Ford Fairlane / Torino GT',
      '1975-1980 Lincoln Versailles (factory disc brake 9" swap donor)',
      '1966-1977 Ford Bronco (narrow housing donor)',
      '1968-1979 Ford F-100 / F-150 Pickup (heavy duty 31-spline center section donor)',
    ],
    interchangeNotes:
      'Universal benchmark rear end. The Lincoln Versailles 1977-1980 housing is a revered donor for early Mustang resto-mods due to factory 58.5" width and rear disc brake setup with integral emergency brake calipers.',
    crossReferenceOEMs: ['Ford C9ZZ-4010-A', 'Currie 9-INCH-UNIV'],
  },
  {
    hollanderNumber: '190-00945',
    category: 'Rear Axle & Differential',
    description: 'Chrysler 8-3/4" (8.75) Rear Axle Housing - #489 Nodular Iron Case',
    yearSpan: '1965-1974',
    compatibleVehicles: [
      '1968-1970 Dodge Charger R/T / Super Bee',
      '1968-1970 Plymouth Road Runner / GTX',
      '1970-1974 Plymouth Barracuda / Cuda',
      '1970-1974 Dodge Challenger R/T',
      '1965-1971 Dodge Coronet / Polara',
    ],
    interchangeNotes:
      'Features robust nodular iron casting #489 with 1-7/8" tapered pinion stem and crush sleeve (or solid collar upgrade). Drops directly into #741 and #742 axle housings. B-Body housing width is 60.7" flange-to-flange; E-Body is 61.6".',
    crossReferenceOEMs: ['Mopar 2881489', 'Yukon YDG-M8.75-30-1'],
  },

  // --- BRAKES & SUSPENSION ---
  {
    hollanderNumber: '530-00624',
    category: 'Brakes & Spindles',
    description: 'Front Disc Brake Spindles & 11" Rotor Assemblies (Single Piston Caliper)',
    yearSpan: '1969-1974',
    compatibleVehicles: [
      '1969-1972 Chevrolet Chevelle / Malibu / El Camino',
      '1969-1972 Pontiac GTO / LeMans / Tempest',
      '1969-1972 Oldsmobile Cutlass / 442 / F-85',
      '1969-1972 Buick Skylark / Special / GS',
      '1969-1974 Chevrolet Nova',
      '1969 Chevrolet Camaro',
    ],
    interchangeNotes:
      'The definitive bolt-on disc brake swap for drum-brake 1964-1972 GM A-Bodies and 1967-1969 F-Bodies. Spindle accepts D52 single-piston cast iron caliper with 7/16-20 banjo bolts. Requires 14" disc-brake profiled wheels or 15"+ rims.',
    crossReferenceOEMs: ['GM 3967425 (LH)', 'GM 3967426 (RH)', 'Right Stuff AFXDC01'],
  },
  {
    hollanderNumber: '530-00710',
    category: 'Brakes & Spindles',
    description: 'GM Tall Spindle Front Disc Conversion (11" / 12" 1LE Brake Setup)',
    yearSpan: '1977-1996',
    compatibleVehicles: [
      '1977-1990 Chevrolet Impala / Caprice (B-Body)',
      '1977-1990 Cadillac Fleetwood / DeVille',
      '1977-1990 Oldsmobile 98 / Custom Cruiser',
      '1989-1992 Chevrolet Camaro 1LE (hub and caliper donor)',
    ],
    interchangeNotes:
      'Used for popular "B-body tall spindle swap" on 1964-1972 A-bodies and 1967-1969 Camaros to correct factory positive camber curve and bump steer. Requires aftermarket tubular upper control arms shortened by 0.5" to 0.75".',
    crossReferenceOEMs: ['GM 18014867', 'Dorman 697-900'],
  },
  {
    hollanderNumber: '530-00815',
    category: 'Brakes & Spindles',
    description: 'Ford Granada 11.0" Front Disc Brake Spindles & Caliper Mounts',
    yearSpan: '1975-1980',
    compatibleVehicles: [
      '1975-1980 Ford Granada',
      '1975-1980 Mercury Monarch',
      '1977-1980 Lincoln Versailles',
      '1965-1973 Ford Mustang (Retrofit conversion)',
      '1967-1973 Mercury Cougar (Retrofit conversion)',
      '1960-1970 Ford Falcon / Fairlane / Ranchero (Retrofit conversion)',
    ],
    interchangeNotes:
      'Classic budget front disc swap for vintage Fords. Bolt-in swap for 1967-1973 Mustang/Cougar. On 1965-1966 cars, requires tie-rod adapter bushings or 1970+ outer tie-rod ends due to larger Granada ball-joint taper pin.',
    crossReferenceOEMs: ['Ford D5DZ-3105-A', 'Cardone 18-4074'],
  },
  {
    hollanderNumber: '515-00388',
    category: 'Front Suspension',
    description: 'Front Upper & Lower Control Arms with Heavy-Duty Ball Joints',
    yearSpan: '1968-1972',
    compatibleVehicles: [
      '1968-1972 Chevrolet Chevelle / Monte Carlo',
      '1968-1972 Pontiac GTO / LeMans',
      '1968-1972 Oldsmobile Cutlass / 442',
      '1968-1972 Buick Skylark / GS',
    ],
    interchangeNotes:
      'Stamped steel wishbone control arms. Upper cross-shaft features round bushings; check for oblong offset shaft on heavy-duty F41 suspension models. Interchangeable across all GM A-body intermediates.',
    crossReferenceOEMs: ['GM 3967401', 'Moog RK620172', 'Global West CTA-42A'],
  },
  {
    hollanderNumber: '515-00445',
    category: 'Front Suspension',
    description: 'Chrysler Heavy-Duty 0.920" Torsion Bars (383/440 Big Block Spec)',
    yearSpan: '1966-1972',
    compatibleVehicles: [
      '1966-1972 Dodge Charger / Coronet R/T / Super Bee',
      '1966-1972 Plymouth Road Runner / GTX / Satellite',
      '1970-1974 Plymouth Barracuda / Cuda (requires E-body 41" bar)',
      '1970-1974 Dodge Challenger (requires E-body 41" bar)',
    ],
    interchangeNotes:
      'Upgrade for slant-6 and 318 small-block cars. B-body torsion bar length is 41.0 inches. Hex-head ends fit into front lower control arm sockets and rear anchor crossmember keys. Inspect for galling or deep pitting.',
    crossReferenceOEMs: ['Mopar 2535894 (RH)', 'Mopar 2535895 (LH)', 'PST TB-6672B'],
  },

  // --- POWERTRAIN, BELLHOUSINGS & TRANSMISSION ---
  {
    hollanderNumber: '400-00621',
    category: 'Transmission & Bellhousing',
    description: 'GM "621" Aluminum 11-Inch Clutch Bellhousing (168-Tooth Flywheel)',
    yearSpan: '1969-1972',
    compatibleVehicles: [
      '1969-1972 Chevrolet Chevelle SS 396 / 454 / LS6',
      '1969 Chevrolet Camaro SS 396 / Z28 302',
      '1970-1972 Chevrolet Monte Carlo 454',
      '1969-1974 Chevrolet Corvette 427 / 454 / 350 LT-1',
      '1969-1972 Chevrolet C10 Pickup (11" clutch truck donor)',
    ],
    interchangeNotes:
      'The premier vintage manual bellhousing. Casting #3899621 has 4.675" transmission bearing retainer bore for Muncie M20, M21, M22, or Borg-Warner Super T10. Allows standard 11" 168-tooth heavy-duty clutch assembly inside car transmission tunnels.',
    crossReferenceOEMs: ['GM 3899621', 'Lakewood 15000', 'Keisler BH-621'],
  },
  {
    hollanderNumber: '400-00403',
    category: 'Transmission & Bellhousing',
    description: 'Mopar A833 4-Speed Manual Transmission - 23-Spline Input (Iron Case)',
    yearSpan: '1966-1974',
    compatibleVehicles: [
      '1968-1970 Dodge Charger / Coronet (383 / 440)',
      '1968-1970 Plymouth Road Runner / GTX (383 / 440)',
      '1970-1974 Plymouth Cuda / Dodge Challenger',
      '1970-1974 Dodge Dart Swinger / Plymouth Duster 340 (A-body short tailshaft)',
    ],
    interchangeNotes:
      'Bulletproof Mopar manual transmission. Tailhousing length must be verified: B-body and E-body use long 30.75" main case with forward or rearward shifter mounting pads. Hurst Competition Plus shifter mounts directly to tailhousing bosses.',
    crossReferenceOEMs: ['Mopar 2801456', 'Passon Performance A833'],
  },
  {
    hollanderNumber: '400-00870',
    category: 'Transmission & Bellhousing',
    description: 'Tremec TKO 500 / 600 & TKX 5-Speed Manual Conversion Crossmember & Mount',
    yearSpan: '1964-1974',
    compatibleVehicles: [
      '1964-1972 GM A-Body (Chevelle, GTO, 442, Skylark)',
      '1967-1969 GM F-Body (Camaro, Firebird)',
      '1965-1973 Ford Mustang / Cougar',
      '1968-1974 Mopar B-Body / E-Body',
    ],
    interchangeNotes:
      'Modern 5-speed overdrive retrofit. Drop-in conversion engineered to clear original GM/Ford floor tunnels without cutting bracing. Uses standard GM 26-spline input shaft and 31-spline output slip yoke.',
    crossReferenceOEMs: ['Tremec TCET17765', 'Silver Sport TR-TKX-600', 'Bowler BMM-600'],
  },

  // --- ELECTRICAL & IGNITION ---
  {
    hollanderNumber: '600-00345',
    category: 'Electrical & Charging',
    description: 'Delco-Remy 12SI High-Output 94-Amp Internal Regulated Alternator',
    yearSpan: '1982-1988',
    compatibleVehicles: [
      '1983-1988 Chevrolet Camaro / Pontiac Firebird (V8)',
      '1983-1987 Chevrolet Monte Carlo SS 305 HO',
      '1984-1987 Buick Regal Grand National / Turbo T',
      '1982-1988 Chevrolet Caprice / Suburban (Heavy Duty Cooling)',
    ],
    interchangeNotes:
      'Best factory bolt-on alternator upgrade for 1964-1974 classic GM muscle. Replaces problematic external regulator and low-output 37A/55A 10DN units. Simple 2-wire pigtail conversion (terminal 1 to indicator light, terminal 2 jumpered to battery post). Clock position typically 12:00 or 3:00.',
    crossReferenceOEMs: ['GM 10479984', 'Delco 10463056', 'Powermaster 8-82100'],
  },
  {
    hollanderNumber: '600-00560',
    category: 'Electrical & Ignition',
    description: 'GM High Energy Ignition (HEI) V8 Vacuum Advance Distributor (Small Cap / Large Cap)',
    yearSpan: '1975-1980',
    compatibleVehicles: [
      '1975-1980 Chevrolet Corvette / Camaro / Chevelle (Small Block 305/350/400)',
      '1975-1980 Chevrolet Trucks C10 / K10 / Suburban',
      '1975-1979 Pontiac Firebird / Trans Am (Pontiac V8 350/400/455)',
      '1975-1979 Oldsmobile Cutlass (Olds Rocket 350/403/455)',
    ],
    interchangeNotes:
      'Replaces vintage breaker point distributors. Internal 4-pin module and coil-in-cap delivers 50,000V. Requires removing vintage ballast resistor or resistance wire to supply full 12V key-on power. Check firewall clearance on 1967-1969 Camaros and 1964-1967 Chevelles.',
    crossReferenceOEMs: ['GM 1112888', 'MSD 8365', 'Performance Distributors DUI-D350'],
  },
  {
    hollanderNumber: '600-00620',
    category: 'Electrical & Charging',
    description: 'Ford 3G Series 130-Amp Internal Fan Alternator (Side Mount)',
    yearSpan: '1990-1996',
    compatibleVehicles: [
      '1992-1996 Ford F-150 / F-250 Pickup (5.0L / 5.8L)',
      '1994-1995 Ford Mustang 5.0L GT / Cobra',
      '1990-1995 Ford Taurus 3.8L',
      '1965-1973 Ford Mustang (Restomod swap donor)',
      '1966-1977 Ford Bronco (High-output swap donor)',
    ],
    interchangeNotes:
      'Standard high-output charging upgrade for vintage Ford engines running electric cooling fans, EFI, or stereo amplifiers. Requires 4-gauge heavy power cable and 175A mega-fuse directly to starter solenoid battery post.',
    crossReferenceOEMs: ['Ford F1PU-10346-CA', 'PA Performance 130A-FORD'],
  },

  // --- SHEET METAL & TRIM ---
  {
    hollanderNumber: '120-00341',
    category: 'Sheet Metal & Body',
    description: 'Front Fender Assemblies (Left & Right Stamped Outer Sheet Metal)',
    yearSpan: '1969',
    compatibleVehicles: [
      '1969 Chevrolet Camaro Coupe & Convertible (Base, RS, SS, Z/28)',
    ],
    interchangeNotes:
      'Unique to 1969 Camaro (does NOT fit 1967 or 1968 due to sharp wheel arch creases and speed lines behind wheel openings). Pontiac Firebird 1969 fenders share inner structure but differ in front header panel and marker cutout locations.',
    crossReferenceOEMs: ['GM 3949821 (LH)', 'GM 3949822 (RH)', 'Auto Metal Direct 110-3569-L'],
  },
  {
    hollanderNumber: '120-00488',
    category: 'Sheet Metal & Body',
    description: 'Hood Assembly - Super Sport Dual Scoop / Cowl Induction Option',
    yearSpan: '1970-1972',
    compatibleVehicles: [
      '1970-1972 Chevrolet Chevelle SS / Malibu',
      '1970-1972 Chevrolet El Camino SS',
    ],
    interchangeNotes:
      'Cowl induction flapper door assembly fits all 1970-1972 Chevelle hoods. Hood hinges and latch pins interchange across all 1970-1972 A-body Chevrolet models. 1968-1969 hoods use completely different cowl length and front brow profile.',
    crossReferenceOEMs: ['GM 3982463', 'Dynacorn 1056J', 'OER 3982463'],
  },
  {
    hollanderNumber: '120-00715',
    category: 'Sheet Metal & Body',
    description: 'Door Shell Assembly (Inner Shell & Outer Skin)',
    yearSpan: '1968-1972',
    compatibleVehicles: [
      '1968-1970 Dodge Charger',
      '1968-1970 Dodge Coronet 2-Door Hardtop',
      '1968-1970 Plymouth Road Runner / Satellite 2-Door',
    ],
    interchangeNotes:
      'Dodge Charger doors have distinctive scalloped door indents. Coronet and Road Runner doors share hinge mounting locations, striker posts, and glass channel geometry but lack Charger door skin scallops.',
    crossReferenceOEMs: ['Mopar 2838708', 'Auto Metal Direct 100-2468-L'],
  },

  // --- COOLING & RADIATORS ---
  {
    hollanderNumber: '700-00215',
    category: 'Cooling & Engine Accessories',
    description: 'High-Flow Aluminum Cross-Flow Radiator - 4-Core Muscle Car Core (28" Wide)',
    yearSpan: '1968-1977',
    compatibleVehicles: [
      '1968-1972 Chevrolet Chevelle / Monte Carlo (Big Block 396/454 & A/C models)',
      '1968-1972 Pontiac GTO / LeMans',
      '1968-1972 Oldsmobile 442',
      '1970-1981 Chevrolet Camaro (V8 HD cooling)',
    ],
    interchangeNotes:
      'Heavy-duty cooling core measuring 28.25" wide x 18.5" high core size. Upper inlet 1.50" on driver side, lower outlet 1.75" on passenger side. Replaces original copper/brass Harrison radiators.',
    crossReferenceOEMs: ['GM 3014745', 'Be Cool 60028', 'Griffin 1-25241-X'],
  },
];

/**
 * Searches the Hollander database by query string (matching number, description, category,
 * compatible vehicles, or OEM part numbers).
 */
export function searchHollanderDatabase(
  query: string,
  categoryFilter?: string
): HollanderInterchangeEntry[] {
  const normalizedQuery = (query || '').trim().toLowerCase();

  return HOLLANDER_INTERCHANGE_DB.filter((entry) => {
    if (categoryFilter && categoryFilter !== 'ALL' && entry.category !== categoryFilter) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    const matchesNumber = entry.hollanderNumber.toLowerCase().includes(normalizedQuery);
    const matchesDesc = entry.description.toLowerCase().includes(normalizedQuery);
    const matchesCat = entry.category.toLowerCase().includes(normalizedQuery);
    const matchesNotes = entry.interchangeNotes.toLowerCase().includes(normalizedQuery);
    const matchesVehicles = entry.compatibleVehicles.some((v) =>
      v.toLowerCase().includes(normalizedQuery)
    );
    const matchesOEMs = entry.crossReferenceOEMs?.some((oem) =>
      oem.toLowerCase().includes(normalizedQuery)
    );

    return (
      matchesNumber ||
      matchesDesc ||
      matchesCat ||
      matchesNotes ||
      matchesVehicles ||
      Boolean(matchesOEMs)
    );
  });
}

/**
 * Finds a Hollander record by exact or partial Hollander number.
 */
export function findHollanderByNumber(
  hollanderNumber: string
): HollanderInterchangeEntry | undefined {
  if (!hollanderNumber) return undefined;
  const clean = hollanderNumber.trim().toLowerCase();
  return HOLLANDER_INTERCHANGE_DB.find(
    (e) => e.hollanderNumber.toLowerCase() === clean
  );
}

/**
 * Returns a unique list of all categories present in the Hollander database.
 */
export function getHollanderCategories(): string[] {
  const set = new Set<string>();
  HOLLANDER_INTERCHANGE_DB.forEach((entry) => set.add(entry.category));
  return Array.from(set).sort();
}

/**
 * Returns compatible donor vehicles for a given Hollander number.
 */
export function getDonorVehiclesForPart(hollanderNumber: string): string[] {
  const found = findHollanderByNumber(hollanderNumber);
  return found ? found.compatibleVehicles : [];
}

/**
 * Generates formatted Parts Inquiries across 4 distinct formats for shop and forum use:
 * 1. Plain Text: Clean unadorned clipboard block for notes, texting, or marketplace.
 * 2. Salvage RFQ: Formatted yard inquiry form with header blocks and instructions.
 * 3. Forum BBCode: Forum-ready markup with bolding and quote blocks for enthusiast boards.
 * 4. Mailto URL: Encoded URL for instant email composition.
 */
export function generatePartsInquiry(
  req: PartsInquiryRequest,
  format: PartsInquiryFormat
): string {
  const item = req.bomItem;
  const conditions =
    req.acceptableConditions && req.acceptableConditions.length > 0
      ? req.acceptableConditions.join(', ')
      : item.condition;
  const hollander = item.hollanderNumber
    ? `Hollander Interchange #: ${item.hollanderNumber}`
    : 'Hollander #: N/A';
  const budget = req.targetBudget
    ? `$${req.targetBudget.toFixed(2)}`
    : item.estimatedCost > 0
    ? `$${item.estimatedCost.toFixed(2)}`
    : 'Competitive Quote';

  switch (format) {
    case 'plain_text':
      return [
        `[PARTS WANTED INQUIRY - RESTOMOD STUDIO]`,
        `Date: ${new Date().toISOString().split('T')[0]}`,
        `Vehicle: ${req.vehicleYearMakeModel} ${req.vin ? `(VIN: ${req.vin})` : ''}`,
        `Component Needed: ${item.partName} (Qty: ${item.quantity || 1})`,
        `OEM Part #: ${item.partNumber || 'Unknown'}`,
        `${hollander}`,
        `Acceptable Conditions: ${conditions}`,
        `Target Budget: ${budget}`,
        `Delivery Zip / Location: ${req.shippingZip || 'Quote with freight'}`,
        `Contact: ${req.contactName} (${req.contactEmailOrPhone})`,
        req.additionalNotes ? `Notes: ${req.additionalNotes}` : '',
      ]
        .filter(Boolean)
        .join('\n');

    case 'salvage_rfq':
      return [
        `=======================================================`,
        `SALVAGE YARD / RECYCLER RFQ INQUIRY`,
        `=======================================================`,
        `DATE: ${new Date().toISOString().split('T')[0]}`,
        `CUSTOMER: ${req.contactName} | ${req.contactEmailOrPhone}`,
        `VEHICLE: ${req.vehicleYearMakeModel}`,
        `PART REQUIRED: ${item.partName}`,
        `HOLLANDER INTERCHANGE: ${item.hollanderNumber || 'Please verify cross-reference'}`,
        `COMPATIBLE DONORS: ${item.donorVehicles?.join('; ') || 'Standard interchange'}`,
        `ACCEPTABLE CONDITIONS: ${conditions}`,
        `CORE RETURN AVAILABLE: ${
          item.coreCharge > 0 ? 'Yes (Clean Core Available)' : 'No (Outright Purchase)'
        }`,
        `DELIVERY DESTINATION: ${req.shippingZip || 'Will pick up / Provide shipping freight'}`,
        `SPECIAL INSTRUCTIONS: Please provide casting/stamping photos and spline/thread condition.`,
        `=======================================================`,
      ].join('\n');

    case 'forum_bbcode':
      return [
        `[b][size=14pt][WTB] ${item.partName} for ${req.vehicleYearMakeModel}[/size][/b]`,
        `[quote]`,
        `[b]Part Name:[/b] ${item.partName}`,
        `[b]Target Application:[/b] ${req.vehicleYearMakeModel}`,
        `[b]OEM Part #:[/b] ${item.partNumber || 'Unknown'}`,
        `[b]Hollander #:[/b] ${item.hollanderNumber || 'N/A'}`,
        `[b]Conditions Accepted:[/b] ${conditions}`,
        `[b]Budget Target:[/b] ${budget}`,
        `[b]Ship To:[/b] ${req.shippingZip || 'Quote to Zip'}`,
        `[/quote]`,
        `Looking for a clean, crack-free unit in working or rebuildable condition. Please PM with pictures and pricing including shipping. Thanks!`,
        req.additionalNotes ? `[i]Additional details: ${req.additionalNotes}[/i]` : '',
      ]
        .filter(Boolean)
        .join('\n');

    case 'mailto_url': {
      const subject = encodeURIComponent(`WTB: ${item.partName} - ${req.vehicleYearMakeModel}`);
      const body = encodeURIComponent(
        `Hi,\n\nI am looking for a ${item.partName} for my ${req.vehicleYearMakeModel}.\n` +
          `OEM Part #: ${item.partNumber || 'Unknown'}\n` +
          `Hollander Interchange: ${item.hollanderNumber || 'N/A'}\n` +
          `Acceptable Conditions: ${conditions}\n` +
          `Budget: ${budget}\n\n` +
          `Please let me know if you have this in stock and send photos/pricing to ${req.contactEmailOrPhone}.\n\n` +
          (req.additionalNotes ? `Notes: ${req.additionalNotes}\n\n` : '') +
          `Thanks,\n${req.contactName}`
      );
      return `mailto:?subject=${subject}&body=${body}`;
    }
  }
}
